import { useCallback, useEffect, useState } from 'react';
import {
  fetchIncidents, reportIncident, recordAbsence, assessRiddor, markReported, updateIncident,
  fetchCompliance, updateCompliance, orgCountry, safetyGate, fetchSafetyPolicies,
  INCIDENT_KINDS, RIDDOR_CATEGORIES, type Incident, type ComplianceItem,
} from '../lib/safety';
import { onFail } from '../lib/errors';
import { useNavigate } from 'react-router-dom';
import './safety.css';

/* =====================================================================================
   Safety: health, safety and wellbeing.

   Three registers. Incidents (the accident book, plus near misses and wellbeing concerns,
   with the statutory reporting clock for a UK organisation), the compliance checklist (what
   the law expects an employer to have in place), and the policies, which live in Canon and
   are listed here so the person looking for them finds them where the incidents are.

   Incidents route to the owner; the reporter always sees their own report. A wellbeing
   concern can be raised confidentially, and the form says exactly who will see it.
   ===================================================================================== */

const SEVERITIES = [['none', 'No harm'], ['minor', 'Minor'], ['moderate', 'Moderate'],
                    ['serious', 'Serious'], ['major', 'Major']] as const;
const fmtDate = (s: string | null | undefined) => (s ? String(s).slice(0, 10) : '');

export function SafetyView() {
  const [tab, setTab] = useState<'incidents' | 'compliance' | 'policies'>('incidents');
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [items, setItems] = useState<ComplianceItem[]>([]);
  const [policies, setPolicies] = useState<Awaited<ReturnType<typeof fetchSafetyPolicies>>>([]);
  const [country, setCountry] = useState<string | null>(null);
  const [gate, setGate] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    fetchIncidents().then(setIncidents).catch(onFail('The incident register', () => setIncidents([])));
    fetchCompliance().then(setItems).catch(onFail('The compliance register', () => setItems([])));
    fetchSafetyPolicies().then(setPolicies).catch(() => setPolicies([]));
    orgCountry().then(setCountry).catch(() => {});
    safetyGate().then(setGate).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const isUK = country === 'GBR';
  const open = incidents.find((i) => i.id === openId) ?? null;
  const due = incidents.filter((i) => i.riddor_reportable && !i.riddor_reported_on && i.riddor_deadline);

  return (
    <div className="page safety">
      <div className="page-head">
        <h1 className="page-title">Safety</h1>
        <p className="page-sub">
          Health, safety and wellbeing: the incident register, the statutory basics, and the
          policies. Anyone can report an incident or a concern; reports go to the owner.
        </p>
      </div>

      {msg && <p className="block-note" role="status">{msg}</p>}

      {/* Statutory deadlines first, because they are the thing with a fine attached. */}
      {gate && due.length > 0 && (
        <div className="saf-riddor-banner">
          {due.map((i) => (
            <div key={i.id}>
              <strong>{i.reference}</strong>: {isUK ? 'RIDDOR report' : 'statutory report'} due
              by <strong>{fmtDate(i.riddor_deadline)}</strong>
              {isUK && <> (report online at hse.gov.uk/riddor, then record the reference here)</>}
              <button className="link-btn" onClick={() => { setTab('incidents'); setOpenId(i.id); }}>
                open
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="toolbar">
        <div className="seg">
          <button className={tab === 'incidents' ? 'on' : ''} onClick={() => setTab('incidents')}>
            Incidents
          </button>
          <button className={tab === 'compliance' ? 'on' : ''} onClick={() => setTab('compliance')}>
            Compliance
          </button>
          <button className={tab === 'policies' ? 'on' : ''} onClick={() => setTab('policies')}>
            Policies
          </button>
        </div>
        <button className="btn primary" style={{ marginLeft: 'auto' }}
                onClick={() => setReporting(true)}>
          Report an incident
        </button>
      </div>

      {tab === 'incidents' && (
        <section className="card">
          {incidents.length === 0 && (
            <p className="module-empty">
              Nothing reported. That is either very good or means nobody knows this page exists:
              near misses count, and reporting one is how the next person avoids the accident.
            </p>
          )}
          {incidents.length > 0 && (
            <table className="data">
              <thead>
                <tr><th>Ref</th><th>What</th><th>When</th><th>Who</th><th>Severity</th>
                    <th>Status</th>{isUK && <th>RIDDOR</th>}<th /></tr>
              </thead>
              <tbody>
                {incidents.map((i) => (
                  <tr key={i.id}>
                    <td className="mono">{i.reference}</td>
                    <td>
                      <div className="cell-title">
                        {INCIDENT_KINDS.find(([k]) => k === i.kind)?.[1] ?? i.kind}
                        {i.confidential && <span className="pill mute saf-conf">confidential</span>}
                      </div>
                      <div className="cell-sub">{i.location}</div>
                    </td>
                    <td className="muted">{fmtDate(i.occurred_at)}</td>
                    <td className="muted">{i.person_name ?? '—'}</td>
                    <td><span className={`pill ${i.severity === 'serious' || i.severity === 'major'
                        ? 'warn' : 'mute'}`}>{i.severity}</span></td>
                    <td><span className={`pill ${i.status === 'closed' ? 'mute' : 'info'}`}>
                        {i.status.replace('_', ' ')}</span></td>
                    {isUK && (
                      <td>
                        {i.riddor_reportable
                          ? (i.riddor_reported_on
                              ? <span className="pill ok" title={i.riddor_reference ?? ''}>reported</span>
                              : <span className="pill warn">due {fmtDate(i.riddor_deadline)}</span>)
                          : <span className="muted">{'—'}</span>}
                      </td>
                    )}
                    <td className="num">
                      <button className="link-btn" onClick={() => setOpenId(i.id)}>Open</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {tab === 'compliance' && (
        <section className="card">
          <p className="muted saf-note">
            The basics the law expects an employer to have, with the statute beside each so the
            reason is never mysterious. Mark what is in place, note where the evidence lives,
            and set a review date so it stays true.
          </p>
          {items.map((c) => (
            <div key={c.id} className="saf-comp">
              <div className="saf-comp-main">
                <div className="saf-comp-title">{c.title}</div>
                {c.statute && <div className="saf-comp-statute">{c.statute}</div>}
                {c.why && <div className="saf-comp-why">{c.why}</div>}
                {c.evidence && <div className="saf-comp-evidence">Evidence: {c.evidence}</div>}
              </div>
              <div className="saf-comp-side">
                <select className="input sel" value={c.status} disabled={!gate}
                        onChange={(e) => updateCompliance(c.id, { status: e.target.value })
                          .then(load).catch(onFail('The register'))}>
                  <option value="not_started">Not started</option>
                  <option value="in_progress">In progress</option>
                  <option value="in_place">In place</option>
                  <option value="needs_attention">Needs attention</option>
                  <option value="not_applicable">Not applicable</option>
                </select>
                {gate && (
                  <>
                    <input className="input" type="date" value={c.review_due ?? ''}
                           title="Review due"
                           onChange={(e) => updateCompliance(c.id, { review_due: e.target.value || null })
                             .then(load).catch(onFail('The register'))} />
                    <button className="link-btn" onClick={() => {
                      const ev = window.prompt('Where does the evidence live? (a folder, a certificate, a date)', c.evidence ?? '');
                      if (ev === null) return;
                      updateCompliance(c.id, { evidence: ev.trim() || null }).then(load).catch(onFail('The register'));
                    }}>evidence</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </section>
      )}

      {tab === 'policies' && (
        <section className="card">
          {policies.length === 0 && (
            <p className="module-empty">No safety policies in Canon yet.</p>
          )}
          {policies.map((p) => (
            <div key={p.id} className="saf-policy">
              <div>
                <span className="mono">{p.doc_id}</span> <strong>{p.title}</strong>
                <span className={`pill ${p.status === 'approved' ? 'ok' : 'mute'}`}>{p.status}</span>
                {p.summary && <div className="cell-sub">{p.summary}</div>}
              </div>
              <button className="link-btn" onClick={() => navigate('/canon')}>Open in Canon</button>
            </div>
          ))}
        </section>
      )}

      {reporting && (
        <ReportForm isUK={isUK}
                    onClose={() => setReporting(false)}
                    onDone={(ref) => { setReporting(false); setMsg(`Reported as ${ref}. The owner has been notified.`); load(); }} />
      )}
      {open && (
        <IncidentDrawer i={open} isUK={isUK} gate={gate}
                        onClose={() => setOpenId(null)} onChanged={load} />
      )}
    </div>
  );
}

function ReportForm({ isUK, onClose, onDone }: {
  isUK: boolean; onClose: () => void; onDone: (ref: string) => void;
}) {
  const [f, setF] = useState({
    kind: 'injury', occurredAt: new Date().toISOString().slice(0, 16), location: '',
    description: '', personName: '', personRole: 'employee', witnesses: '', injuryDetail: '',
    immediateAction: '', severity: 'minor', confidential: false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const wellbeing = f.kind === 'wellbeing_concern';

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer saf-drawer">
        <h2>Report an incident</h2>
        <p className="muted">
          Injuries, ill health, near misses, dangerous occurrences and wellbeing concerns all
          belong here. The report goes to the owner{isUK
            ? ', and the statutory RIDDOR clock is handled from what you record.'
            : '.'}
        </p>
        {err && <p className="block-note">{err}</p>}
        <label className="cf-field"><span>What happened</span>
          <select className="input sel" value={f.kind} onChange={set('kind')}>
            {INCIDENT_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></label>
        {wellbeing && (
          <label className="saf-check">
            <input type="checkbox" checked={f.confidential}
                   onChange={(e) => setF({ ...f, confidential: e.target.checked })} />
            <span>Raise confidentially. Only the owner and HR can read it; your line manager
            and colleagues cannot.</span>
          </label>
        )}
        <label className="cf-field"><span>When</span>
          <input className="input" type="datetime-local" value={f.occurredAt} onChange={set('occurredAt')} /></label>
        <label className="cf-field"><span>Where</span>
          <input className="input" value={f.location} onChange={set('location')}
                 placeholder="Building, room, site, or home if homeworking" /></label>
        <label className="cf-field"><span>What happened, in your own words</span>
          <textarea className="input" rows={4} value={f.description} onChange={set('description')} /></label>
        {!wellbeing && (
          <>
            <label className="cf-field"><span>Who was involved</span>
              <input className="input" value={f.personName} onChange={set('personName')}
                     placeholder="Name, or blank if nobody was hurt" /></label>
            <label className="cf-field"><span>They are</span>
              <select className="input sel" value={f.personRole} onChange={set('personRole')}>
                <option value="employee">An employee</option>
                <option value="contractor">A contractor</option>
                <option value="visitor">A visitor</option>
                <option value="public">A member of the public</option>
                <option value="other">Other</option>
              </select></label>
            <label className="cf-field"><span>Injury, if any</span>
              <input className="input" value={f.injuryDetail} onChange={set('injuryDetail')}
                     placeholder="Nature and part of body, or blank" /></label>
            <label className="cf-field"><span>Witnesses</span>
              <input className="input" value={f.witnesses} onChange={set('witnesses')} /></label>
            <label className="cf-field"><span>What was done immediately</span>
              <input className="input" value={f.immediateAction} onChange={set('immediateAction')}
                     placeholder="First aid given, area closed, equipment taken out of use..." /></label>
            <label className="cf-field"><span>How serious</span>
              <select className="input sel" value={f.severity} onChange={set('severity')}>
                {SEVERITIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select></label>
          </>
        )}
        <div className="exp-decide-actions">
          <button className="btn primary" disabled={busy || !f.location.trim() || !f.description.trim()}
                  onClick={async () => {
                    setBusy(true); setErr(null);
                    try {
                      await reportIncident(f);
                      onDone('a new incident');
                    } catch (e) { setErr(String((e as Error).message)); }
                    finally { setBusy(false); }
                  }}>
            Report it
          </button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}

function IncidentDrawer({ i, isUK, gate, onClose, onChanged }: {
  i: Incident; isUK: boolean; gate: boolean; onClose: () => void; onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const act = (fn: () => Promise<unknown>) => async () => {
    setBusy(true); setErr(null);
    try { await fn(); onChanged(); } catch (e) { setErr(String((e as Error).message)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer saf-drawer">
        <h2>{i.reference} <span className="pill info">{i.status.replace('_', ' ')}</span></h2>
        {err && <p className="block-note">{err}</p>}
        <dl className="saf-dl">
          <dt>What</dt><dd>{INCIDENT_KINDS.find(([k]) => k === i.kind)?.[1] ?? i.kind}</dd>
          <dt>When</dt><dd>{String(i.occurred_at).replace('T', ' ').slice(0, 16)}</dd>
          <dt>Where</dt><dd>{i.location}</dd>
          <dt>Description</dt><dd>{i.description}</dd>
          {i.person_name && <><dt>Who</dt><dd>{i.person_name} ({i.person_role})</dd></>}
          {i.injury_detail && <><dt>Injury</dt><dd>{i.injury_detail}</dd></>}
          {i.witnesses && <><dt>Witnesses</dt><dd>{i.witnesses}</dd></>}
          {i.immediate_action && <><dt>Immediate action</dt><dd>{i.immediate_action}</dd></>}
        </dl>

        {gate && i.kind === 'injury' && (
          <section className="saf-block">
            <h3>Absence due to injury at work</h3>
            <p className="muted">
              First full day away and the return date. {isUK && 'Passing seven days makes this '
              + 'RIDDOR-reportable within 15 days of the accident; that is computed for you.'}
            </p>
            <div className="saf-absence">
              <label>First day absent
                <input className="input" type="date" defaultValue={fmtDate(i.absence_started_on)} id={`ab-${i.id}`} /></label>
              <label>Returned
                <input className="input" type="date" defaultValue={fmtDate(i.returned_on)} id={`ret-${i.id}`} /></label>
              <button className="btn" disabled={busy} onClick={act(async () => {
                const first = (document.getElementById(`ab-${i.id}`) as HTMLInputElement).value;
                const ret = (document.getElementById(`ret-${i.id}`) as HTMLInputElement).value;
                if (!first) throw new Error('The first day absent is needed.');
                await recordAbsence(i.id, first, ret || undefined);
              })}>Record</button>
            </div>
            {i.days_incapacitated != null && (
              <p className="muted">{i.days_incapacitated} day(s) incapacitated
                {i.returned_on ? '' : ' so far (still absent)'}.</p>
            )}
          </section>
        )}

        {gate && isUK && (
          <section className="saf-block">
            <h3>RIDDOR</h3>
            <div className="saf-absence">
              <select className="input sel" value={i.riddor_category ?? ''}
                      disabled={busy || !!i.riddor_reported_on}
                      onChange={(e) => act(() => assessRiddor(i.id, e.target.value || null))()}>
                <option value="">Not reportable</option>
                {RIDDOR_CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            {i.riddor_reportable && !i.riddor_reported_on && (
              <p className="saf-due">Report to the HSE by <strong>{fmtDate(i.riddor_deadline)}</strong>{' '}
                at hse.gov.uk/riddor, then{' '}
                <button className="link-btn" disabled={busy} onClick={act(async () => {
                  const ref = window.prompt('The HSE report reference (from the F2508 submission):');
                  if (ref === null) throw new Error('Not recorded.');
                  await markReported(i.id, ref);
                })}>record the reference</button>.</p>
            )}
            {i.riddor_reported_on && (
              <p className="muted">Reported {fmtDate(i.riddor_reported_on)}
                {i.riddor_reference && <> , reference {i.riddor_reference}</>}.</p>
            )}
          </section>
        )}

        {gate && (
          <section className="saf-block">
            <h3>Investigation</h3>
            {(['investigation', 'root_cause', 'actions'] as const).map((k) => (
              <div key={k} className="saf-inv">
                <strong>{k === 'root_cause' ? 'Root cause' : k[0].toUpperCase() + k.slice(1)}</strong>
                {i[k] && <p>{i[k]}</p>}
                <button className="link-btn" disabled={busy} onClick={act(async () => {
                  const v = window.prompt(k === 'actions'
                    ? 'What will change so this does not happen again?'
                    : 'Write it as you would want to read it in a year.', i[k] ?? '');
                  if (v === null) throw new Error('Not saved.');
                  await updateIncident(i.id, { [k]: v.trim() || null });
                })}>{i[k] ? 'edit' : 'add'}</button>
              </div>
            ))}
            <div className="exp-decide-actions">
              {i.status !== 'closed' && (
                <button className="btn" disabled={busy} onClick={act(() =>
                  updateIncident(i.id, { status: i.status === 'open' ? 'under_review' : 'closed',
                                         ...(i.status !== 'open' ? { closed_at: new Date().toISOString() } : {}) }))}>
                  {i.status === 'open' ? 'Start review' : 'Close'}
                </button>
              )}
              <button className="btn" onClick={onClose}>Done</button>
            </div>
          </section>
        )}
      </div>
    </>
  );
}
